import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Dumbbell, Trash2, Loader2, X, Plus, ChevronUp, ChevronDown
} from 'lucide-react';
import client from '../api/client';
import { format } from 'date-fns';
import { SET_TYPES, SetTypePopover } from './workout/sharedSetTypes';
import ExercisePicker from './ExercisePicker';
import StaticExerciseImage from './StaticExerciseImage';
import { TARGET_MUSCLE_COLORS } from '../lib/muscleUtils';
import { translateMuscle, translateEquipment } from '../lib/muscleTranslations';
import { exerciseName, defaultSetCount, getExerciseInputMode, getCardioFields } from '../lib/exerciseDisplay';
import { useLanguage } from '../contexts/LanguageContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useConfirm } from '../hooks/useConfirm';
import UnsavedChangesAlert from './UnsavedChangesAlert';

// WorkoutEditDialog set-row grid, keyed by the number of *value* columns
// between the set# button and the delete button:
//   reps   → set# | KG | Reps | del            → 2 value cols  (unchanged literal)
//   time   → set# | mm:ss     | del             → 1 value col
//   cardio → set# | MIN | metric×n | del        → 1 + n value cols
// Static literals so Tailwind JIT keeps them.
const EDIT_SET_GRID = {
  1: 'grid-cols-[40px_1fr_28px] sm:grid-cols-[44px_1fr_32px]',                 // set# + 1 value + del   (time, or cardio 0-metric)
  2: 'grid-cols-[40px_1fr_1fr_28px] sm:grid-cols-[44px_1fr_1fr_32px]',         // set# + 2 values + del  (reps; cardio 1-metric)
  3: 'grid-cols-[40px_1fr_1fr_1fr_28px] sm:grid-cols-[44px_1fr_1fr_1fr_32px]', // set# + 3 values + del  (cardio 2-metric)
};

export default function WorkoutEditDialog({ session, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { language } = useLanguage();
  const [editExercises, setEditExercises] = useState([]);
  const [editDuration, setEditDuration] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [removeExerciseConfirm, setRemoveExerciseConfirm] = useState(null);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [exchangeExerciseIdx, setExchangeExerciseIdx] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const { markDirty, markClean, requestClose, confirmProps } = useUnsavedChanges();

  // Enrich exercises from DB when dialog opens
  React.useEffect(() => {
    if (open && session && !initialized) {
      initEdit(session);
    }
    if (!open) {
      setInitialized(false);
      setEditExercises([]);
      setEditDuration(0);
      markClean();
    }
  }, [open, session]);

  const initEdit = async (sess) => {
    setEditDuration(sess.duration_minutes || 0);
    setEnriching(true);
    
    const exercises = JSON.parse(JSON.stringify(sess.exercises || []));
    const names = exercises.map(ex => ex.name);
    
    let enrichedData = {};
    try {
      const res = await client.post('/exercises/enrich-batch', names);
      enrichedData = res.data;
    } catch (err) { toast.error(err?.response?.data?.detail || t('workoutEdit.failedToEnrich')); }

    const enrichedExercises = exercises.map(ex => {
      const dbEx = enrichedData[ex.name] || {};
      return {
        ...ex,
        exercise_id: ex.exercise_id || dbEx.id || dbEx.exerciseId || '',
        targetMuscles: ex.targetMuscles?.length ? ex.targetMuscles : (dbEx.targetMuscles || dbEx.primary_muscles || []),
        secondaryMuscles: ex.secondaryMuscles?.length ? ex.secondaryMuscles : (dbEx.secondaryMuscles || dbEx.secondary_muscles || []),
        gifUrl: dbEx.gifUrl || dbEx.image_url || ex.gifUrl || '',
        equipment: dbEx.equipment || ex.equipment || '',
        muscle_group: ex.muscle_group || '',
        name: dbEx.name || ex.name,
        sets: (ex.sets || []).map((s, i) => ({
          ...s,
          set_number: s.set_number || i + 1,
          set_type: s.set_type || 'work',
          weight: s.weight || 0,
          completed_reps: s.completed_reps || s.reps || 0,
          duration_seconds: s.duration_seconds || 0,
          speed_kmh: s.speed_kmh || 0,
          incline_pct: s.incline_pct || 0,
          distance_m: s.distance_m || 0,
          resistance_level: s.resistance_level || 0,
        })),
      };
    });

    setEditExercises(enrichedExercises);
    setEnriching(false);
    setInitialized(true);
  };

  const updateSetField = (exIdx, setIdx, field, value) => {
    markDirty();
    setEditExercises(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[exIdx].sets[setIdx][field] = value;
      return copy;
    });
  };

  const changeSetType = (exIdx, setIdx, newType) => {
    markDirty();
    setEditExercises(prev => prev.map((ex, i) =>
      i === exIdx ? {
        ...ex, sets: ex.sets.map((s, j) => j === setIdx ? { ...s, set_type: newType } : s)
      } : ex
    ));
  };

  const deleteSet = (exIdx, setIdx) => {
    markDirty();
    setEditExercises(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[exIdx].sets = copy[exIdx].sets.filter((_, i) => i !== setIdx);
      copy[exIdx].sets.forEach((s, i) => { s.set_number = i + 1; });
      return copy;
    });
  };

  const removeExercise = (exIdx) => {
    markDirty();
    setEditExercises(prev => prev.filter((_, i) => i !== exIdx));
  };

  const addSet = (exIdx) => {
    markDirty();
    setEditExercises(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const lastSet = copy[exIdx].sets[copy[exIdx].sets.length - 1];
      copy[exIdx].sets.push({
        set_number: copy[exIdx].sets.length + 1,
        set_type: lastSet?.set_type || 'work',
        target_reps_min: lastSet?.target_reps_min || 8,
        target_reps_max: lastSet?.target_reps_max || 12,
        weight: lastSet?.weight || 0,
        completed_reps: lastSet?.completed_reps || 0,

        is_pr: false, pr_types: [],
      });
      return copy;
    });
  };

  const addExerciseFromPicker = (exercise) => {
    markDirty();
    setEditExercises(prev => [...prev, {
      id: exercise.id || crypto.randomUUID(),
      exercise_id: exercise.id || '',
      name: exercise.name,
      muscle_group: exercise.muscle_group || '',
      targetMuscles: exercise.targetMuscles || exercise.primary_muscles || [],
      secondaryMuscles: exercise.secondaryMuscles || exercise.secondary_muscles || [],
      gifUrl: exercise.gifUrl || exercise.image_url || '',
      equipment: exercise.equipment || '',
      notes: '',
      sets: Array.from({ length: defaultSetCount(exercise) }, (_, i) => (
        { set_number: i + 1, set_type: 'work', target_reps_min: 8, target_reps_max: 12, weight: 0, completed_reps: 0, is_pr: false }
      ))
    }]);
    setShowAddExercise(false);
    toast.success(t('workoutEdit.addedExercise', { name: exercise.name }));
  };

  const replaceExerciseFromPicker = (exercise) => {
    markDirty();
    setEditExercises(prev => prev.map((ex, i) =>
      i === exchangeExerciseIdx ? {
        ...ex,
        exercise_id: exercise.id || '',
        name: exercise.name,
        muscle_group: exercise.muscle_group || '',
        targetMuscles: exercise.targetMuscles || exercise.primary_muscles || [],
        secondaryMuscles: exercise.secondaryMuscles || exercise.secondary_muscles || [],
        gifUrl: exercise.gifUrl || exercise.image_url || '',
        equipment: exercise.equipment || '',
      } : ex
    ));
    setExchangeExerciseIdx(null);
    toast.success(t('workoutEdit.replacedExercise', { name: exercise.name }));
  };

  const moveExercise = (fromIdx, direction) => {
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= editExercises.length) return;
    markDirty();
    setEditExercises(prev => {
      const copy = [...prev];
      [copy[fromIdx], copy[toIdx]] = [copy[toIdx], copy[fromIdx]];
      return copy;
    });
  };

  const handleSave = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const exercisesPayload = editExercises.map(ex => ({
        id: ex.id,
        exercise_id: ex.exercise_id || '',
        name: ex.name,
        muscle_group: ex.muscle_group || '',
        targetMuscles: ex.targetMuscles || [],
        secondaryMuscles: ex.secondaryMuscles || [],
        notes: ex.notes || '',
        sets: ex.sets.map(s => ({
          set_number: s.set_number,
          set_type: s.set_type || 'work',
          target_reps_min: s.target_reps_min || 0,
          target_reps_max: s.target_reps_max || 0,
          weight: s.weight || 0,
          completed_reps: s.completed_reps || 0,
          duration_seconds: s.duration_seconds || 0,
          speed_kmh: s.speed_kmh || 0,
          incline_pct: s.incline_pct || 0,
          distance_m: s.distance_m || 0,
          resistance_level: s.resistance_level || 0,
          is_pr: s.is_pr || false, pr_types: s.pr_types || [],
          rpe: s.rpe || null,
        })),
      }));

      await client.put(`/workout-sessions/${session.id}`, {
        routine_id: session.routine_id,
        day_id: session.day_id,
        exercises: exercisesPayload,
        duration_minutes: editDuration,
      });
      toast.success(t('workoutEdit.workoutUpdated'));
      markClean();
      onClose();
      if (onSaved) onSaved();
    } catch {
      toast.error(t('workoutEdit.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!session) return;
    const ok = await confirm({
      title: t('workoutEdit.confirmDelete'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await client.delete(`/workout-sessions/${session.id}`);
      toast.success(t('workoutEdit.workoutDeleted'));
      onClose();
      if (onSaved) onSaved();
    } catch {
      toast.error(t('workoutEdit.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(val) => { if (!val) requestClose(onClose); }}>
        <DialogContent className="max-w-2xl max-h-[calc(100dvh-160px)] sm:max-h-[90dvh] overflow-y-auto p-0" hideCloseButton data-testid="edit-workout-dialog">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-background border-b border-border px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-lg">
                {t('workoutEdit.editWorkout')} <span className="text-primary">{t('workoutEdit.workoutLabel')}</span>
              </DialogTitle>
              <UITooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => requestClose(onClose)} className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 flex items-center justify-center rounded-sm opacity-70 hover:opacity-100 active:bg-muted transition-opacity" aria-label="Close">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltips.close')}</TooltipContent>
              </UITooltip>
            </div>
            {session && (
              <div className="flex items-center justify-between mt-3">
                <div>
                  <p className="font-bold text-sm">{session.day_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {session.completed_at ? format(new Date(session.completed_at), 'MMM d, yyyy · h:mm a') : t('workoutEdit.dateUnavailable')}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">{t('workoutEdit.duration')}</Label>
                  <Input type="number" inputMode="numeric" value={editDuration} onChange={(e) => { markDirty(); setEditDuration(parseInt(e.target.value) || 0); }}
                    className="w-20 h-11 md:h-8 text-base md:text-sm" data-testid="edit-duration-input" />
                  <span className="text-xs text-muted-foreground">min</span>
                  <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 text-muted-foreground hover:text-destructive ml-1"
                    tooltip={t('tooltips.deleteWorkout')}
                    onClick={handleDelete} disabled={deleting} title="Delete workout" data-testid="delete-workout-btn">
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {enriching ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-3 text-muted-foreground">{t('workoutEdit.loadingExerciseData')}</span>
            </div>
          ) : session && (
            <div className="px-4 sm:px-6 py-4 space-y-4">
              {editExercises.map((ex, exIdx) => {
                const muscles = ex.targetMuscles || [];
                const exInputMode = getExerciseInputMode(ex);
                const exCardioFields = exInputMode === 'cardio' ? getCardioFields(ex) : [];
                const valueCols = exInputMode === 'cardio' ? (1 + exCardioFields.length) : exInputMode === 'time' ? 1 : 2;
                const setGridClass = EDIT_SET_GRID[valueCols] || EDIT_SET_GRID[2];
                return (
                  <div key={`${ex.name}-${exIdx}`} className="border border-border/50 rounded-xl overflow-hidden" data-testid={`edit-exercise-${exIdx}`}>
                    {/* Exercise Header */}
                    <div className="flex items-center gap-3 px-3 py-3 bg-muted/30">
                      <div className="flex flex-col gap-0.5">
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => moveExercise(exIdx, -1)} disabled={exIdx === 0}
                              className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 flex items-center justify-center text-muted-foreground hover:text-foreground active:bg-muted disabled:opacity-20 md:p-0.5" data-testid={`move-up-${exIdx}`}>
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>{t('tooltips.moveUp')}</TooltipContent>
                        </UITooltip>
                        <UITooltip>
                          <TooltipTrigger asChild>
                            <button onClick={() => moveExercise(exIdx, 1)} disabled={exIdx === editExercises.length - 1}
                              className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 flex items-center justify-center text-muted-foreground hover:text-foreground active:bg-muted disabled:opacity-20 md:p-0.5" data-testid={`move-down-${exIdx}`}>
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>{t('tooltips.moveDown')}</TooltipContent>
                        </UITooltip>
                      </div>

                      {ex.gifUrl && (
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted border border-border flex-shrink-0">
                          <StaticExerciseImage src={ex.gifUrl} alt={ex.name} size="sm" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 overflow-hidden">
                          <p className="font-semibold text-sm truncate min-w-0">{exerciseName(ex, language)}</p>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {muscles.length > 0 ? (
                            muscles.map(m => (
                              <span key={m} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border capitalize ${TARGET_MUSCLE_COLORS[m.toLowerCase()] || 'bg-muted text-muted-foreground border-border'}`}>
                                {translateMuscle(m, language)}
                              </span>
                            ))
                          ) : ex.muscle_group ? (
                            <span className="text-xs text-muted-foreground capitalize">{translateMuscle(ex.muscle_group, language)}</span>
                          ) : null}
                          {ex.equipment && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border bg-blue-500/10 text-blue-400 border-blue-500/20">
                              {translateEquipment(ex.equipment, language)}
                            </span>
                          )}
                        </div>
                      </div>

                      <Button variant="ghost" size="icon" className="h-11 w-11 md:h-8 md:w-8 text-muted-foreground hover:text-destructive shrink-0"
                        tooltip={t('tooltips.removeExercise')}
                        onClick={() => setRemoveExerciseConfirm(exIdx)} data-testid={`remove-exercise-${exIdx}`}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Sets Table */}
                    <div className="px-3 py-2">
                      <div className={`grid ${setGridClass} gap-1.5 sm:gap-2 px-1 mb-1.5`}>
                        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workoutEdit.set')}</span>
                        {exInputMode === 'cardio' ? (
                          <>
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colMinutes')}</span>
                            {exCardioFields.map((f) => (
                              <span key={f.key} className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t(f.headerKey)}</span>
                            ))}
                          </>
                        ) : exInputMode === 'time' ? (
                          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">{t('workout.colTime')}</span>
                        ) : (
                          <>
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">KG</span>
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider text-center">Reps</span>
                          </>
                        )}
                        <span></span>
                      </div>

                      <div className="space-y-1">
                        {ex.sets.map((set, setIdx) => {
                          const setType = SET_TYPES[set.set_type] || SET_TYPES.work;
                          return (
                            <div key={setIdx} className={`grid ${setGridClass} gap-1.5 sm:gap-2 items-center px-1 py-1`}>
                              <SetTypePopover
                                currentType={set.set_type || 'work'}
                                onSelect={(type) => changeSetType(exIdx, setIdx, type)}
                                onDelete={() => deleteSet(exIdx, setIdx)}
                              >
                                <button
                                  className={`w-11 h-11 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-colors cursor-pointer ${setType.color} text-white active:scale-95`}
                                  data-testid={`set-type-btn-${exIdx}-${setIdx}`}
                                >
                                  {set.set_number}
                                </button>
                              </SetTypePopover>

                              {exInputMode === 'cardio' ? (
                                <>
                                  <Input type="number" inputMode="numeric" min="0"
                                    value={(() => { const tot = parseInt(set.duration_seconds, 10); return (tot === undefined || tot === null || isNaN(tot)) ? '' : Math.floor(tot / 60); })()}
                                    onChange={(e) => updateSetField(exIdx, setIdx, 'duration_seconds', (parseInt(e.target.value, 10) || 0) * 60)}
                                    className="h-11 md:h-9 text-center font-mono text-base md:text-sm" placeholder="min"
                                    data-testid={`edit-duration-min-${exIdx}-${setIdx}`} aria-label={t('workout.colMinutes')} />
                                  {exCardioFields.map((f) => (
                                    <Input key={f.key} type="number" inputMode={f.htmlInputMode} step={f.step} min="0"
                                      value={f.toDisplay(set[f.key])}
                                      onChange={(e) => updateSetField(exIdx, setIdx, f.key, f.fromInput(e.target.value))}
                                      className="h-11 md:h-9 text-center font-mono text-base md:text-sm" placeholder={f.placeholder}
                                      data-testid={`edit-cardio-${f.key}-${exIdx}-${setIdx}`} aria-label={t(f.headerKey)} />
                                  ))}
                                </>
                              ) : exInputMode === 'time' ? (
                                (() => {
                                  const hasVal = set.duration_seconds !== undefined && set.duration_seconds !== null && set.duration_seconds !== '';
                                  const totSec = parseInt(set.duration_seconds, 10) || 0;
                                  const mm = hasVal ? Math.floor(totSec / 60) : '';
                                  const ss = hasVal ? (totSec % 60) : '';
                                  return (
                                    <div className="flex items-center gap-1 justify-center">
                                      <Input type="number" inputMode="numeric" min="0"
                                        value={mm}
                                        onChange={(e) => { const cur = parseInt(set.duration_seconds, 10) || 0; updateSetField(exIdx, setIdx, 'duration_seconds', (parseInt(e.target.value, 10) || 0) * 60 + (cur % 60)); }}
                                        className="h-11 md:h-9 w-full text-center font-mono text-base md:text-sm" placeholder="mm"
                                        data-testid={`edit-duration-min-${exIdx}-${setIdx}`} aria-label={t('workout.colTime') + ' minutes'} />
                                      <span className="text-xs text-muted-foreground font-mono">:</span>
                                      <Input type="number" inputMode="numeric" min="0" max="59"
                                        value={ss}
                                        onChange={(e) => { const m = Math.floor((parseInt(set.duration_seconds, 10) || 0) / 60); updateSetField(exIdx, setIdx, 'duration_seconds', m * 60 + Math.min(59, parseInt(e.target.value, 10) || 0)); }}
                                        className="h-11 md:h-9 w-full text-center font-mono text-base md:text-sm" placeholder="ss"
                                        data-testid={`edit-duration-sec-${exIdx}-${setIdx}`} aria-label={t('workout.colTime') + ' seconds'} />
                                    </div>
                                  );
                                })()
                              ) : (
                                <>
                                  <Input type="number" inputMode="decimal" step="0.5" value={set.weight || ''}
                                    onChange={(e) => updateSetField(exIdx, setIdx, 'weight', parseFloat(e.target.value) || 0)}
                                    className="h-11 md:h-9 text-center font-mono text-base md:text-sm" placeholder="0"
                                    data-testid={`edit-weight-${exIdx}-${setIdx}`} />

                                  <Input type="number" inputMode="numeric" value={set.completed_reps || ''}
                                    onChange={(e) => updateSetField(exIdx, setIdx, 'completed_reps', parseInt(e.target.value) || 0)}
                                    className="h-11 md:h-9 text-center font-mono text-base md:text-sm" placeholder="0"
                                    data-testid={`edit-reps-${exIdx}-${setIdx}`} />
                                </>
                              )}

                              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-7 md:w-7 min-w-[36px] md:min-w-[28px] text-muted-foreground hover:text-destructive"
                                tooltip={t('tooltips.removeSet')}
                                onClick={() => deleteSet(exIdx, setIdx)} data-testid={`remove-set-${exIdx}-${setIdx}`}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>

                      <Button variant="ghost" size="sm" className="w-full h-11 md:h-8 mt-1 text-xs text-muted-foreground hover:text-primary"
                        onClick={() => addSet(exIdx)} data-testid={`add-set-${exIdx}`}>
                        <Plus className="h-3 w-3 mr-1" /> {t('workoutEdit.addSet')}
                      </Button>
                    </div>
                  </div>
                );
              })}

              {editExercises.length === 0 && (
                <p className="text-center text-muted-foreground py-4 text-sm">{t('workoutEdit.noExercises')}</p>
              )}

              <button onClick={() => setShowAddExercise(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-border hover:border-primary/50 text-muted-foreground hover:text-primary transition-all group"
                data-testid="edit-add-exercise-btn">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/90 transition-colors">
                  <Plus className="h-5 w-5 text-primary" />
                </div>
                <span className="font-semibold text-sm">{t('workoutEdit.addExercise')}</span>
              </button>
            </div>
          )}

          {/* Sticky footer */}
          {session && !enriching && (
            <div className="sticky bottom-0 bg-background border-t border-border px-4 sm:px-6 py-3">
              <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSave} disabled={saving} data-testid="save-workout-edit-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('workoutEdit.saving')}</> : t('workoutEdit.saveChanges')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Exercise Picker — add */}
      <ExercisePicker
        open={showAddExercise}
        onClose={() => setShowAddExercise(false)}
        onSelect={addExerciseFromPicker}
        title={t('workoutEdit.addExerciseTitle')}
      />

      {/* Exercise Picker — replace */}
      <ExercisePicker
        open={exchangeExerciseIdx !== null}
        onClose={() => setExchangeExerciseIdx(null)}
        onSelect={replaceExerciseFromPicker}
        title={t('workoutEdit.replaceExerciseTitle')}
      />

      {/* Unsaved changes guard */}
      <UnsavedChangesAlert {...confirmProps} />

      {/* Remove Exercise Confirmation */}
      <AlertDialog open={removeExerciseConfirm !== null} onOpenChange={(open) => { if (!open) setRemoveExerciseConfirm(null); }}>
        <AlertDialogContent data-testid="remove-exercise-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workoutEdit.removeExercise')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workoutEdit.removeExerciseDesc', { name: removeExerciseConfirm !== null ? editExercises[removeExerciseConfirm]?.name : '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-exercise-cancel-btn">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90" data-testid="remove-exercise-confirm-btn"
              onClick={() => { removeExercise(removeExerciseConfirm); setRemoveExerciseConfirm(null); }}>
              {t('common.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
